// The daily routine's calls to the server (/api/routine, routine/RoutineController). Effects only: what they mean
// is domain/routine.ts.

import type { EmptyDay, RecentDays, RoutineDay, RoutineItem } from "../domain/routine.ts";

async function post<T = void>(path: string, body?: unknown, keepalive = false): Promise<T> {
    const response = await fetch(path, {
        method: "POST",
        credentials: "include",
        keepalive,
        headers: { "Content-Type": "application/json" },
        body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
        throw new Error(`The routine could not be updated: HTTP ${response.status}`);
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
}

const base = (day: string) => `/api/routine/${encodeURIComponent(day)}`;

/** A day ("today" or yyyy-MM-dd). Null without a session; a day not come yet is an error. */
export async function fetchDay(day: string): Promise<RoutineDay | EmptyDay | null> {
    const response = await fetch(base(day), { credentials: "include" });
    if (response.status === 401) return null;
    if (!response.ok) {
        throw new Error(`The routine could not be read: HTTP ${response.status}`);
    }
    return response.json();
}

/** The first visit to a day, and where it came from. Written once; later visits change nothing. */
export function markOpened(day: string, from: "web" | "email" | "home"): Promise<void> {
    return post(`${base(day)}/open`, { from }, true);
}

export function markStarted(day: string, slot: number): Promise<void> {
    return post(`${base(day)}/items/${slot}/start`, undefined, true);
}

/** The opening as a line the study can play: one of theirs, or one made now and kept out of their lists. */
export function openingLine(day: string, slot: number): Promise<{ lineId: number }> {
    return post(`${base(day)}/items/${slot}/line`);
}

/** The opening played to the end once more. */
export function addPlay(day: string, slot: number): Promise<RoutineItem> {
    return post(`${base(day)}/items/${slot}/play`, undefined, true);
}

/** A moment answered. Only the first answer counts for the routine. */
export function markDone(day: string, slot: number, result: "solved" | "partial" | "missed" | "skipped"): Promise<void> {
    return post(`${base(day)}/items/${slot}/done`, { result }, true);
}

/** What the user thought of the day, 1 to 5, and anything they want to add. */
export function rate(day: string, rating: number, comment: string): Promise<void> {
    return post(`${base(day)}/feedback`, { rating, comment });
}

/** The days a routine was started lately, and today in the reader's clock: the home's "your week". Null signed out. */
export async function fetchRecent(days = 60): Promise<RecentDays | null> {
    const response = await fetch(`/api/routine/recent?days=${days}`, { credentials: "include" });
    if (response.status === 401) return null;
    if (!response.ok) {
        throw new Error(`The recent days could not be read: HTTP ${response.status}`);
    }
    return response.json();
}
